import { Droppable, Draggable } from '@hello-pangea/dnd'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TareaCard, TareaGrupoCard } from './TareaCard'
import type { TareaCompleta } from '@/types/database'
import type { GrupoDeTablero, ItemTablero } from '@/core/services/tareasGrupoService'
import { cn } from '@/lib/utils'

interface KanbanColumnaProps {
  id: string
  titulo: string
  /** Tareas sueltas y grupos, en el orden en que se muestran. */
  items: ItemTablero[]
  colorScheme?: 'estado' | 'temporal'
  onVerDetalle?: (tarea: TareaCompleta) => void
  onEditar?: (tarea: TareaCompleta) => void
  onEliminar?: (tarea: TareaCompleta) => void
  onVerGrupo?: (grupo: GrupoDeTablero) => void
  canEdit?: boolean | ((tarea: TareaCompleta) => boolean)
  canDelete?: boolean
}

interface ColumnStyle {
  bg: string
  border: string
  badge: string
  title: string
}

const ESTADO_STYLES: Record<string, ColumnStyle> = {
  pendiente: {
    bg: 'bg-slate-50 dark:bg-slate-900/50',
    border: 'border-slate-200 dark:border-slate-700',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    title: 'text-slate-900 dark:text-slate-100',
  },
  en_proceso: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    title: 'text-blue-900 dark:text-blue-100',
  },
  completada: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    title: 'text-green-900 dark:text-green-100',
  },
}

const TEMPORAL_STYLES: Record<string, ColumnStyle> = {
  vencida: {
    bg: 'bg-rose-50 dark:bg-rose-900/30',
    border: 'border-rose-300 dark:border-rose-800',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300',
    title: 'text-rose-900 dark:text-rose-100',
  },
  urgente: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    title: 'text-red-900 dark:text-red-100',
  },
  hoy: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    title: 'text-amber-900 dark:text-amber-100',
  },
  esta_semana: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    title: 'text-blue-900 dark:text-blue-100',
  },
  este_mes: {
    bg: 'bg-slate-50 dark:bg-slate-900/50',
    border: 'border-slate-200 dark:border-slate-700',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    title: 'text-slate-900 dark:text-slate-100',
  },
  sin_fecha: {
    bg: 'bg-gray-50 dark:bg-gray-900/50',
    border: 'border-gray-200 dark:border-gray-700',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    title: 'text-gray-900 dark:text-gray-100',
  },
  completada: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    title: 'text-green-900 dark:text-green-100',
  },
}

const DEFAULT_STYLE: ColumnStyle = {
  bg: 'bg-slate-50 dark:bg-slate-900/50',
  border: 'border-slate-200 dark:border-slate-700',
  badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  title: 'text-slate-900 dark:text-slate-100',
}

export function KanbanColumna({
  id,
  titulo,
  items,
  colorScheme = 'estado',
  onVerDetalle,
  onEditar,
  onEliminar,
  onVerGrupo,
  canEdit = true,
  canDelete = true,
}: KanbanColumnaProps) {
  const styleMap = colorScheme === 'temporal' ? TEMPORAL_STYLES : ESTADO_STYLES
  const styles = styleMap[id] ?? DEFAULT_STYLE

  const puedeEditarFila = (tarea: TareaCompleta) =>
    typeof canEdit === 'function' ? canEdit(tarea) : canEdit

  return (
    <Card
      className={cn(
        'flex flex-col h-full min-w-[300px] max-w-[350px]',
        styles.bg,
        styles.border
      )}
    >
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className={cn('text-sm font-medium', styles.title)}>{titulo}</CardTitle>
          <Badge variant="secondary" className={cn('text-xs', styles.badge)}>
            {items.length}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 p-2">
        <Droppable droppableId={id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(
                'space-y-2 min-h-[200px] h-full overflow-y-auto p-1 rounded-lg transition-colors duration-200',
                snapshot.isDraggingOver && 'bg-primary/5 ring-2 ring-primary/20'
              )}
            >
              {items.length === 0 && !snapshot.isDraggingOver ? (
                <div className="flex items-center justify-center h-[100px] text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                  Sin tareas
                </div>
              ) : (
                items.map((item, index) => {
                  if (item.tipo === 'grupo') {
                    // Un grupo no se arrastra. Moverlo cambiaría tareas que el usuario quizás no
                    // puede editar. Ocupa un índice, así el de cada tarea sigue siendo su lugar.
                    return (
                      <Draggable key={`grupo:${item.grupo.clave}`} draggableId={`grupo:${item.grupo.clave}`} index={index} isDragDisabled>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <TareaGrupoCard grupo={item.grupo} onVerGrupo={onVerGrupo} />
                          </div>
                        )}
                      </Draggable>
                    )
                  }
                  const tarea = item.tarea
                  if (!tarea.id) return null
                  return (
                    <Draggable key={tarea.id!} draggableId={tarea.id!} index={index} isDragDisabled={!puedeEditarFila(tarea)}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...(puedeEditarFila(tarea) ? provided.dragHandleProps : {})}
                          style={{
                            ...provided.draggableProps.style,
                            transition: snapshot.isDropAnimating
                              ? 'all 0.12s cubic-bezier(0.2, 0, 0, 1)'
                              : provided.draggableProps.style?.transition,
                          }}
                        >
                          <TareaCard
                            tarea={tarea}
                            isDragging={snapshot.isDragging}
                            onVerDetalle={onVerDetalle}
                            onEditar={onEditar}
                            onEliminar={onEliminar}
                            canEdit={puedeEditarFila(tarea)}
                            canDelete={canDelete}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                })
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </CardContent>
    </Card>
  )
}
